<template>
    <PageTemplate>
        <BentoHero
            title="Share your medical video content, safely and securely."
            description="MedPeers enables medical professionals to share their knowledge with peers worldwide."
            data-span-all
        />
        <GraphicDevice
            class="image-fill-md"
            data-span-5
        >
            <NuxtPicture
                src="/stock/doctor-4.jpg"
                alt=""
                sizes="xs:100vw, md:40vw"
                width="300px"
                height="300px"
                loading="lazy"
                decoding="async"
                style="--object-position: top;"
                :img-attrs="{
                    fetchpriority: 'low'
                }"
            />
        </GraphicDevice>
        <BentoFrame
            data-span-3
        >
            <h2>Submit a video before June 1st 2024 and become a founding member with 1 year free access.</h2>
            <BaseButton
                :element="defineNuxtLink({})"
                to="/how-to-make-a-video"
                gtm-location="hero"
            >
                How to make a video
            </BaseButton>
        </BentoFrame>
        <BentoFrame
            data-span-all
            class="content-width"
            dark
            title="How it works:"
        >
            <div>
                <h2>Create a short video in a few simple steps. Make sure to:</h2>
                <DetailsList
                    :items="[
                        {
                            icon: 'assignment-turned-in',
                            title: 'Get consent',
                            description: 'Get consent from your patient'
                        },
                        {
                            icon: 'contacts',
                            title: 'Start filming',
                            description: `We’ll give you some <a href='/how-to-make-a-video'>tips & tricks</a>`
                        },
                        {
                            icon: 'rotate-right',
                            title: 'Submit your video',
                            description: 'Videos will be quality reviewed'
                        },
                        {
                            icon: 'docs-add-on',
                            title: 'Get a founding member status',
                            description: 'Receive one year free access'
                        }
                    ]"
                />
            </div>
        </BentoFrame>
        <BentoFrame
            data-span-all
            class="content-width"
        >
            <h2>Share your skills with peers.</h2>
            <BaseButton
                :element="defineNuxtLink({})"
                to="/upload/checklist"
            >
                Submit your video
            </BaseButton>
        </BentoFrame>
        <BaseSection
            data-span-all
            section-width="content"
        >
            <template #title>
                Become a founding member.
            </template>
            <template #description>
                <p>We are grateful to all our founding members contributing to our platform. By submitting your video content, you'll automatically become a founding member and receive extra benefits:</p>
            </template>
            <IconList
                :items="[
                    {
                        icon: 'check',
                        description: 'Be one of the first to get full access once the platform is launched'
                    }, {
                        icon: 'check',
                        description: 'Receive one year free of charge'
                    }, {
                        icon: 'check',
                        description: 'Your name will be added to the founding member list'
                    }
                ]"
            />
        </BaseSection>

        <BaseSection
            data-span-all
            style="margin-top: -2rem; margin-bottom: 2rem;"
            section-width="bleed"
        >
            <FoundingMembers />
        </BaseSection>

        <BentoFrame data-span-all>
            <template #image>
                <GraphicDevice
                    :shape="2"
                    data-span-5
                >
                    <NuxtPicture
                        src="/stock/doctor-2.png"
                        alt=""
                        sizes="xs:100vw, md:40vw"
                        width="300px"
                        height="300px"
                        loading="lazy"
                        decoding="async"
                        style="--object-position: top;"
                        :img-attrs="{
                            fetchpriority: 'low'
                        }"
                    />
                </GraphicDevice>
            </template>
            <h2>Secure video content peer-reviewed by medical professionals</h2>
            <BaseButton
                :element="defineNuxtLink({})"
                to="/about-us"
            >
                Learn more about MedPeers
            </BaseButton>
        </BentoFrame>
        <BaseSection
            data-span-all
            section-width="content"
        >
            <template #title>Frequently asked questions</template>
            <BaseAccordion
                :items="[
                    {
                        title: 'How can I upload my video content?',
                        content: `Please find instructions on how to upload your video content on our <a href='/how-to-make-a-video'>how to make a video</a> guide.`
                    },
                    {
                        title: 'How to create high quality video content?',
                        content: `MedPeers believes healthcare professionals are able to produce high quality and educational video content themselves. We have some tips and tricks to help you out, please have a look at our <a href='/how-to-make-a-video'>how to make a video</a> guide.`
                    },
                    {
                        title: 'What does the peer-review process entail?',
                        content: 'Uploaded video content will be reviewed by at least two independent experts in your field. Though you will remain responsible for the submitted work, they will check the quality, educational value, scientific integrity and patient anonymity.'
                    },
                    {
                        title: 'Who will have access to my video?',
                        content: 'MedPeers is exclusively accessible to verified and licensed healthcare professionals.'
                    }
                ]"
            />
        </BaseSection>
    </PageTemplate>
</template>

<script setup>
const { asset } = useBaseUrl();

useSeoMeta({
    title: 'MedPeers | Share your knowledge and upload your video',
    description: 'A secure, peer-reviewed and quality-controlled platform offering educational videos of medical procedures',
    ogImage: asset('/images/og/default.png')
});
</script>
